import { Fragment, useCallback, useEffect, useRef } from 'react';
import clsx from 'clsx';

import type { AiChatMessagePart, Widget } from '../ai-chat-types';

import { AI_MESSAGE_ROLES } from '@highlight-cp/constants';
import { Box, Divider, Flex, Stack, Text } from '@highlight-cp/core-ui';

import { AiChatWindowImage, isImagePart } from '../ai-chat-window-image';
import AiChatWindowLoader from '../ai-chat-window-loader';
import AiChatWindowMessage from '../ai-chat-window-message';
import { FeedbackOptions } from '../feedback-options';
import classes from './ai-chat-messages.module.css';
import type { AiChatMessagesProps, RenderPartContext } from './types';
import { defaultIsPartVisible } from './utils/default-is-part-visible';
import { getAssistantMessageText } from './utils/get-assistant-message-text';

const EMPTY_HIDDEN_MESSAGE_IDS = new Set<string>();
const EMPTY_WIDGETS_BY_MESSAGE_ID = new Map<string, Widget>();

// Generic line shown while responding. Features that can say something more specific pass their own
// via `thinkingMessage` (see feature-persona-chat's use-persona-chat-thinking-message, and
// feature-insights-chat's get-insights-chat-progress-message).
const DEFAULT_THINKING_MESSAGE = 'Thinking...';

const AiChatMessages = ({
  className,
  messages,
  isLoading,
  thinkingMessage,
  showThinkingIndicator = true,
  thinkingLoader,
  hiddenMessageIds = EMPTY_HIDDEN_MESSAGE_IDS,
  isPartVisible,
  renderPart,
  widgetsByMessageId = EMPTY_WIDGETS_BY_MESSAGE_ID,
  ownsScroll = true,
  onThumbsUp,
  onThumbsDown,
  onThumbsDownFeedback,
  onTryAgain,
  showCopy = false,
  isAnswerMessage,
  renderMessageMeta,
  renderMessageActions,
  messageGap = 'lg',
  showMessageActionsDivider = false,
}: AiChatMessagesProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // When the caller owns the scroll container this box does not scroll, so the
    // follow-to-bottom belongs to the caller and running it here would be a dead
    // timer against a non-scrolling element.
    if (!ownsScroll) {
      return undefined;
    }

    const container = scrollContainerRef.current;
    if (!container || typeof container.scrollTo !== 'function') {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth',
      });
    }, 1);

    return () => window.clearTimeout(timeout);
  }, [messages, isLoading, ownsScroll]);

  const resolvedThinkingMessage = thinkingMessage ?? DEFAULT_THINKING_MESSAGE;

  const checkPartVisible = useCallback(
    (part: AiChatMessagePart, ctx: RenderPartContext) => {
      if (isPartVisible) {
        return isPartVisible(part, ctx);
      }

      if (renderPart && part.type.startsWith('tool-')) {
        return true;
      }

      return defaultIsPartVisible(part, ctx);
    },
    [isPartVisible, renderPart],
  );

  const renderDefaultPart = useCallback((part: AiChatMessagePart, ctx: RenderPartContext) => {
    if (part.type === 'text') {
      return (
        <AiChatWindowMessage
          content={part.text ?? ''}
          role={ctx.message.role}
          index={ctx.messageIndex}
        />
      );
    }

    if (isImagePart(part)) {
      return <AiChatWindowImage part={part} role={ctx.message.role} />;
    }

    return null;
  }, []);

  return (
    <Box ref={scrollContainerRef} className={clsx(ownsScroll && classes.root, className)}>
      <Box className={classes.container}>
        <Stack gap="lg" mb="lg">
          {messages.map((message, messageIndex) => {
            const isHidden = hiddenMessageIds instanceof Set && hiddenMessageIds.has(message.id);
            const widget = widgetsByMessageId.get(message.id);
            const isLastMessage = messageIndex === messages.length - 1;
            const isStreaming =
              Boolean(isLoading) && isLastMessage && message.role === AI_MESSAGE_ROLES.ASSISTANT;

            const visibleParts = message.parts
              .map((part, partIndex) => {
                const ctx: RenderPartContext = {
                  message,
                  messageIndex,
                  partIndex,
                  isStreaming,
                };

                if (!checkPartVisible(part, ctx)) {
                  return null;
                }

                const rendered = renderPart ? renderPart(part, ctx) : renderDefaultPart(part, ctx);

                if (rendered == null) {
                  return null;
                }

                return <Fragment key={`${message.id}-${partIndex}`}>{rendered}</Fragment>;
              })
              .filter(Boolean);

            const hasVisibleContent = !isHidden && visibleParts.length > 0;

            if (!hasVisibleContent && !widget) {
              return null;
            }

            const nextMessage = messages[messageIndex + 1];
            const isAssistantMessage = message.role === AI_MESSAGE_ROLES.ASSISTANT;
            const isCompletedAssistant = isAssistantMessage && !isStreaming;

            // Copy: every completed assistant message when the caller opts in — unless the surface
            // says this particular message is not an answer, in which case there is nothing worth
            // handing over as one.
            const shouldShowCopy =
              isCompletedAssistant && showCopy && (isAnswerMessage?.(message) ?? true);

            // Thumbs / try-again: last message only (existing behavior).
            const shouldShowFeedbackOptions =
              isAssistantMessage &&
              (!isLoading || !isLastMessage) &&
              messageIndex > 0 &&
              !nextMessage &&
              (onThumbsUp != null ||
                onThumbsDown != null ||
                onThumbsDownFeedback != null ||
                onTryAgain != null);

            const leadingActions = isCompletedAssistant
              ? (renderMessageActions?.(message, messageIndex) ?? null)
              : null;

            const shouldShowMessageActions =
              shouldShowCopy || shouldShowFeedbackOptions || leadingActions != null;
            const messageMeta = isStreaming ? null : renderMessageMeta?.(message, messageIndex);
            const copyValue = shouldShowCopy ? getAssistantMessageText(message) : undefined;
            const hasAnswerText = Boolean(copyValue && copyValue.length > 0);

            const handleMessageThumbsUp = onThumbsUp
              ? () => {
                  onThumbsUp(messageIndex);
                }
              : undefined;
            const handleMessageThumbsDown = onThumbsDown
              ? () => {
                  onThumbsDown(messageIndex);
                }
              : undefined;
            const handleMessageThumbsDownFeedback = onThumbsDownFeedback
              ? (feedback: string) => {
                  onThumbsDownFeedback(messageIndex, feedback);
                }
              : undefined;

            const messageActions = shouldShowMessageActions ? (
              <FeedbackOptions
                index={messageIndex}
                copyValue={hasAnswerText ? copyValue : undefined}
                copyAsHtml={hasAnswerText}
                showCopy={shouldShowCopy}
                onThumbsUp={shouldShowFeedbackOptions ? handleMessageThumbsUp : undefined}
                onThumbsDown={shouldShowFeedbackOptions ? handleMessageThumbsDown : undefined}
                onThumbsDownFeedback={
                  shouldShowFeedbackOptions ? handleMessageThumbsDownFeedback : undefined
                }
                onTryAgain={
                  shouldShowFeedbackOptions && onTryAgain ? () => onTryAgain(message.id) : undefined
                }
                leadingActions={leadingActions}
              />
            ) : null;

            const hasActionRow = messageMeta != null || messageActions != null;

            return (
              <Stack key={message.id} gap={messageGap}>
                {!isHidden && visibleParts}
                {widget ? widget() : null}
                {showMessageActionsDivider && hasActionRow && !widget ? <Divider /> : null}
                {messageMeta == null ? (
                  messageActions
                ) : (
                  <Flex align="center" gap="sm" justify="space-between">
                    <Box miw={0}>{messageMeta}</Box>
                    {messageActions}
                  </Flex>
                )}
              </Stack>
            );
          })}
        </Stack>
        {isLoading && showThinkingIndicator && (
          <Stack gap="sm">
            {thinkingLoader ?? <AiChatWindowLoader />}
            <Text size="sm" c="dimmed" role="status">
              {resolvedThinkingMessage}
            </Text>
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default AiChatMessages;
